import React, { useState } from 'react';
import { Upload, X, FileSpreadsheet, AlertCircle, CheckCircle, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { apiRequest } from '../../../api/core.js';
import { downloadDriverTemplate } from '../../../utils/importTemplates.js';
import {
  asciiFold, parseLicenseCategories, categoriesToText, categoriesToBooleans,
  normalizeLicenseCountry,
} from '../../../utils/driverLicense.js';

/**
 * Import de conducteurs depuis un fichier Excel (Lot G — « Reprise »).
 * Jumeau de VehicleImportModal : parsing côté client, mapping de colonnes
 * tolérant (alias FR/EN), prévisualisation, erreurs par ligne, puis
 * POST /drivers/bulk-import. L'org d'écriture est décidée côté serveur à
 * partir de l'en-tête X-Active-Org — la filiale active, pas l'org primaire.
 */

// Alias acceptés par en-tête de colonne (minuscules, accents retirés).
// Le chantier Groupe est multi-pays : les en-têtes polonais et suédois sont
// reconnus au même titre que le français et l'anglais.
const COLUMN_ALIASES = {
  last_name: ['nom de famille', 'nom', 'last name', 'lastname', 'surname', 'family name', 'nazwisko', 'efternamn'],
  first_name: ['prenom', 'first name', 'firstname', 'given name', 'imie', 'fornamn'],
  email: ['email', 'e-mail', 'e mail', 'courriel', 'mail', 'e post', 'epost'],
  phone: ['telephone', 'tel', 'phone', 'mobile', 'portable', 'telefon'],
  license_number: ['n de permis', 'numero de permis', 'permis', 'license number', 'licence number', 'driving licence', 'driver license', 'prawo jazdy', 'nr prawa jazdy', 'korkort', 'korkortsnummer'],
  license_expiry: ['date d expiration du permis', 'expiration du permis', 'expiration', 'validite permis', 'license expiry', 'licence expiry', 'expiry date', 'data waznosci', 'waznosc', 'giltigt till', 'giltighetstid'],
  license_issue_date: ['date de delivrance du permis', 'delivrance', 'license issue', 'licence issue', 'issue date', 'data wydania', 'utfardat'],
  license_categories: ['categories de permis', 'categorie de permis', 'categories', 'categorie', 'license categories', 'licence categories', 'kategoria', 'kategorie', 'kategoria prawa jazdy', 'behorighet', 'behorigheter'],
  license_country: ['pays de delivrance', 'pays du permis', 'pays', 'issuing country', 'licence country', 'license country', 'country', 'kraj', 'kraj wydania', 'land', 'utfardandeland'],
  hire_date: ['date d embauche', 'embauche', 'hire date', 'hired', 'start date', 'data zatrudnienia', 'anstallningsdatum'],
  birth_date: ['date de naissance', 'naissance', 'birth date', 'date of birth', 'dob', 'data urodzenia', 'fodelsedatum'],
  status: ['statut', 'status', 'etat'],
  address: ['adresse', 'address', 'adres', 'adress'],
};

// `asciiFold` (util partagé) gère ce que NFD ne décompose pas : `ł` polonais,
// `å`/`ø` nordiques. Les autres (ż, ą, ö, ä, ę) passent par NFD.
const norm = s => asciiFold(s)
  .replace(/[^a-z0-9]+/gi, ' ')
  .trim();

function DriverImportModal({ isOpen, onClose, onSuccess, onMessage }) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [summary, setSummary] = useState(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
      onMessage?.({
        title: t('drivers.messages.errorTitle'),
        message: t('drivers.importModal.errors.badExtension'),
        type: 'error',
      });
      return;
    }

    setFile(selectedFile);
    setErrors([]);
    setSummary(null);

    try {
      const data = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array', cellDates: true });

      // Feuille : « Conducteurs » si elle existe, sinon la première.
      const sheetName = workbook.SheetNames.find(n => /conducteur|driver/i.test(n)) || workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });

      // Ligne d'en-tête : la première (dans les 20 premières) qui porte un nom
      // ET un email ou un permis reconnaissables.
      let headerRowIndex = -1;
      for (let i = 0; i < Math.min(jsonData.length, 20); i++) {
        const cells = (jsonData[i] || []).map(norm);
        const hasName = cells.some(c => c && COLUMN_ALIASES.last_name.includes(c));
        const hasKey = cells.some(c => c && (COLUMN_ALIASES.email.includes(c) || COLUMN_ALIASES.license_number.includes(c)));
        if (hasName && hasKey) { headerRowIndex = i; break; }
      }
      if (headerRowIndex === -1) throw new Error(t('drivers.importModal.errors.noHeaderRow'));

      const headers = jsonData[headerRowIndex];
      const dataRows = jsonData
        .slice(headerRowIndex + 1)
        .map((row, idx) => ({ row, sheetRow: headerRowIndex + 2 + idx }))
        .filter(({ row }) => row && row.some(cell => cell !== null && cell !== ''));

      setParsedData(parseDrivers(headers, dataRows));
    } catch (error) {
      console.error('Error parsing driver file:', error);
      onMessage?.({
        title: t('drivers.messages.errorTitle'),
        message: t('drivers.importModal.errors.parseFailed', { message: error.message }),
        type: 'error',
      });
    }
  };

  const parseDrivers = (headers, dataRows) => {
    const drivers = [];
    const parseErrors = [];

    // Finder tolérant : correspondance exacte sur un alias d'abord, sinon
    // « l'en-tête contient l'alias » (les exports ajoutent souvent un suffixe).
    const col = (aliases) => {
      const normalized = headers.map(norm);
      let idx = normalized.findIndex(h => h && aliases.includes(h));
      if (idx === -1) idx = normalized.findIndex(h => h && aliases.some(a => h.includes(a)));
      return idx;
    };
    const cell = (row, idx) => (idx >= 0 && row[idx] != null && row[idx] !== '' ? row[idx] : null);
    const text = (row, idx) => { const v = cell(row, idx); return v == null ? null : v.toString().trim() || null; };
    const date = (row, idx) => {
      const v = cell(row, idx);
      if (v == null) return null;
      if (v instanceof Date && !isNaN(v)) return v.toLocaleDateString('sv-SE'); // AAAA-MM-JJ local
      if (typeof v === 'number' && v > 20000 && v < 80000) {
        return new Date(Date.UTC(1899, 11, 30) + v * 86400000).toISOString().slice(0, 10);
      }
      return v.toString().trim() || null;
    };

    const idx = Object.fromEntries(Object.entries(COLUMN_ALIASES).map(([k, aliases]) => [k, col(aliases)]));

    dataRows.forEach(({ row, sheetRow }) => {
      try {
        const lastName = text(row, idx.last_name);
        const firstName = text(row, idx.first_name);
        const email = text(row, idx.email);
        const license = text(row, idx.license_number);
        const expiry = date(row, idx.license_expiry);

        // L'email n'est PAS obligatoire : un fichier client réel a des
        // conducteurs sans adresse. Le serveur pose alors un bouche-trou
        // `pasdemailNNN@non-renseigne.invalid`, non routable (RFC 2606).
        const missing = [];
        if (!lastName) missing.push(t('drivers.importModal.fields.lastName'));
        if (!firstName) missing.push(t('drivers.importModal.fields.firstName'));
        if (!license) missing.push(t('drivers.importModal.fields.licenseNumber'));
        if (!expiry) missing.push(t('drivers.importModal.fields.licenseExpiry'));
        if (missing.length) {
          parseErrors.push({ row: sheetRow, error: t('drivers.importModal.errors.missingFields', { fields: missing.join(', ') }) });
          return;
        }

        // Catégories UE 2006/126/CE : parser tolérant partagé avec le backend
        // (« B,CE », « kat. B », « behörighet CE », « poids lourd »…).
        const rawCats = text(row, idx.license_categories);
        const { categories, unknown } = parseLicenseCategories(rawCats);
        if (unknown.length) {
          parseErrors.push({
            row: sheetRow,
            warning: true,
            error: t('drivers.importModal.warnings.unknownCategories', { values: unknown.join(', ') }),
          });
        }

        const rawCountry = text(row, idx.license_country);
        const country = normalizeLicenseCountry(rawCountry);
        if (rawCountry && !country) {
          parseErrors.push({
            row: sheetRow,
            warning: true,
            error: t('drivers.importModal.warnings.unknownCountry', { value: rawCountry }),
          });
        }

        drivers.push({
          _row: sheetRow,
          last_name: lastName,
          first_name: firstName,
          email: email || null,
          phone: text(row, idx.phone),
          license_number: license,
          license_expiry: expiry,
          license_issue_date: date(row, idx.license_issue_date),
          license_country: country,
          hire_date: date(row, idx.hire_date),
          birth_date: date(row, idx.birth_date),
          status: text(row, idx.status) || '',
          address: text(row, idx.address),
          license_categories: categoriesToText(categories),
          ...categoriesToBooleans(categories),
          _categories: categories.join(', '),
        });
      } catch (error) {
        parseErrors.push({ row: sheetRow, error: error.message });
      }
    });

    setErrors(parseErrors);
    return drivers;
  };

  const handleImport = async () => {
    if (!parsedData || parsedData.length === 0) {
      onMessage?.({
        title: t('drivers.messages.errorTitle'),
        message: t('drivers.importModal.errors.nothingToImport'),
        type: 'error',
      });
      return;
    }

    setImporting(true);
    try {
      const res = await apiRequest('/drivers/bulk-import', {
        method: 'POST',
        body: JSON.stringify({ drivers: parsedData }),
      });
      const r = res.data || res || {};
      const created = r.imported || 0;
      const updated = r.updated || 0;
      const failures = r.failed || 0;

      setSummary({
        total: parsedData.length,
        created,
        updated,
        failures,
        placeholders: r.placeholder_emails || 0,
        orgId: r.org_id,
        countryDefault: r.license_country_default,
        errors: r.errors || [],
        warnings: r.warnings || [],
      });

      if (created + updated > 0) {
        onMessage?.({
          title: failures === 0 ? t('drivers.messages.successTitle') : t('drivers.importModal.partialTitle'),
          message: t('drivers.importModal.resultMessage', { created, updated, failures }),
          type: failures === 0 ? 'success' : 'warning',
        });
        // Rafraîchir la liste dès qu'une ligne est passée, même si d'autres ont échoué.
        await onSuccess?.(parsedData);
      } else {
        onMessage?.({
          title: t('drivers.messages.errorTitle'),
          message: t('drivers.importModal.errors.noneImported'),
          type: 'error',
        });
      }
    } catch (error) {
      setSummary({
        total: parsedData.length, created: 0, updated: 0, failures: parsedData.length,
        errors: [{ row: '-', driver: t('drivers.importModal.title'), error: error.message }],
      });
      onMessage?.({
        title: t('drivers.messages.errorTitle'),
        message: t('drivers.importModal.errors.importFailed', { message: error.message }),
        type: 'error',
      });
    }
    setImporting(false);
  };

  const handleReset = () => {
    setFile(null);
    setParsedData(null);
    setErrors([]);
    setSummary(null);
  };

  const handleClose = () => {
    handleReset();
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* En-tête */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-6 h-6" />
              <h2 className="text-2xl font-bold">{t('drivers.importModal.title')}</h2>
            </div>
            <button onClick={handleClose} className="text-white hover:text-gray-200" aria-label={t('common.actions.close', { defaultValue: 'Fermer' })}>
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Consignes + modèle */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">{t('drivers.importModal.instructionsTitle')}</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• {t('drivers.importModal.instructions.required')}</li>
                  <li>• {t('drivers.importModal.instructions.optional')}</li>
                  <li>• {t('drivers.importModal.instructions.email')}</li>
                  <li>• {t('drivers.importModal.instructions.categories')}</li>
                  <li>• {t('drivers.importModal.instructions.country')}</li>
                  <li>• {t('drivers.importModal.instructions.dedup')}</li>
                  <li>• {t('drivers.importModal.instructions.activeOrg')}</li>
                </ul>
              </div>
              <button
                onClick={() => downloadDriverTemplate()}
                className="shrink-0 px-4 py-2 bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-100 flex items-center gap-2 text-sm font-medium"
              >
                <Download className="w-4 h-4" />
                {t('drivers.importModal.downloadTemplate')}
              </button>
            </div>
          </div>

          {/* Choix du fichier */}
          {!parsedData && !summary && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
              <div className="text-center">
                <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <label className="cursor-pointer">
                  <span className="text-blue-600 hover:text-blue-700 font-medium">
                    {t('drivers.importModal.selectFile')}
                  </span>
                  <input type="file" accept=".xlsx,.xls" onChange={handleFileSelect} className="hidden" />
                </label>
                <p className="text-sm text-gray-500 mt-2">{t('drivers.importModal.selectFileHint')}</p>
              </div>
            </div>
          )}

          {/* Prévisualisation */}
          {parsedData && !summary && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900">
                  {t('drivers.importModal.previewTitle', { count: parsedData.length })}
                </h3>
                <button onClick={handleReset} className="text-sm text-gray-600 hover:text-gray-900">
                  {t('drivers.importModal.changeFile')}
                </button>
              </div>

              {file && <p className="text-xs text-gray-500 mb-3">{file.name}</p>}

              {errors.length > 0 && (() => {
                // Deux natures : la ligne est écartée (erreur) ou seulement
                // amputée d'une valeur illisible (avertissement).
                const skipped = errors.filter(e => !e.warning);
                const warns = errors.filter(e => e.warning);
                const render = (list, title, tone) => list.length > 0 && (
                  <div className={`${tone.box} border rounded-lg p-4 mb-4`}>
                    <div className="flex items-start gap-2">
                      <AlertCircle className={`w-5 h-5 ${tone.icon} flex-shrink-0 mt-0.5`} />
                      <div>
                        <p className={`font-medium ${tone.title}`}>{title}</p>
                        <ul className={`text-sm ${tone.text} mt-2 space-y-1 max-h-32 overflow-y-auto`}>
                          {list.slice(0, 10).map((err, i) => (
                            <li key={i}>{t('drivers.importModal.rowLabel', { row: err.row })}: {err.error}</li>
                          ))}
                          {list.length > 10 && (
                            <li className="font-medium">{t('drivers.importModal.moreErrors', { count: list.length - 10 })}</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
                return (
                  <>
                    {render(skipped, t('drivers.importModal.parseErrors', { count: skipped.length }),
                      { box: 'bg-red-50 border-red-200', icon: 'text-red-600', title: 'text-red-900', text: 'text-red-800' })}
                    {render(warns, t('drivers.importModal.parseWarnings', { count: warns.length }),
                      { box: 'bg-amber-50 border-amber-200', icon: 'text-amber-600', title: 'text-amber-900', text: 'text-amber-800' })}
                  </>
                );
              })()}

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-96">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('drivers.importModal.columns.name')}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('drivers.importModal.columns.email')}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('drivers.importModal.columns.phone')}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('drivers.importModal.columns.license')}</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('drivers.importModal.columns.expiry')}</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('drivers.importModal.columns.categories')}</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('drivers.importModal.columns.country')}</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('drivers.importModal.columns.status')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {parsedData.slice(0, 50).map((d, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm font-medium text-gray-900">{d.first_name} {d.last_name}</td>
                          <td className="px-3 py-2 text-sm text-gray-600">
                            {d.email || (
                              <span className="flex items-center gap-1.5 text-red-600 italic">
                                <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" title={t('drivers.missingEmail.tooltip')} />
                                {t('drivers.importModal.emailWillBeFilled')}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600">{d.phone || '-'}</td>
                          <td className="px-3 py-2 text-sm font-mono text-gray-700">{d.license_number}</td>
                          <td className="px-3 py-2 text-sm text-center text-gray-600">{d.license_expiry}</td>
                          <td className="px-3 py-2 text-center text-xs text-gray-500">{d._categories || '-'}</td>
                          <td className="px-3 py-2 text-center text-xs text-gray-500">{d.license_country || <span className="italic text-gray-400">{t('drivers.importModal.countryFromOrg')}</span>}</td>
                          <td className="px-3 py-2 text-center text-xs text-gray-500">{d.status || t('drivers.status.active')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {parsedData.length > 50 && (
                  <div className="bg-gray-50 px-4 py-2 text-sm text-gray-600 text-center">
                    {t('drivers.importModal.moreRows', { count: parsedData.length - 50 })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Résumé */}
          {summary && (
            <div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-green-900 text-lg">{t('drivers.importModal.doneTitle')}</h3>
                    <div className="mt-2 space-y-1 text-sm text-green-800">
                      <p>{t('drivers.importModal.summaryTotal', { count: summary.total })}</p>
                      <p>✓ {t('drivers.importModal.summaryCreated', { count: summary.created })}</p>
                      <p>↻ {t('drivers.importModal.summaryUpdated', { count: summary.updated })}</p>
                      {summary.failures > 0 && <p>✗ {t('drivers.importModal.summaryFailed', { count: summary.failures })}</p>}
                      {summary.placeholders > 0 && (
                        <p className="flex items-center gap-1.5 text-red-700 font-medium">
                          <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" />
                          {t('drivers.importModal.summaryPlaceholders', { count: summary.placeholders })}
                        </p>
                      )}
                      {summary.orgId && <p className="text-xs text-green-700">{t('drivers.importModal.summaryOrg', { org: summary.orgId })}</p>}
                      {summary.countryDefault && <p className="text-xs text-green-700">{t('drivers.importModal.summaryCountry', { country: summary.countryDefault })}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {summary.warnings?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <p className="font-medium text-amber-900 mb-2">{t('drivers.importModal.importWarnings')}</p>
                  <ul className="text-sm text-amber-800 space-y-1 max-h-40 overflow-y-auto">
                    {summary.warnings.map((w, i) => (
                      <li key={i}>{t('drivers.importModal.rowLabel', { row: w.row })} — {w.driver}: {w.warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {summary.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="font-medium text-red-900 mb-2">{t('drivers.importModal.importErrors')}</p>
                  <ul className="text-sm text-red-800 space-y-1 max-h-48 overflow-y-auto">
                    {summary.errors.map((err, i) => (
                      <li key={i}>{t('drivers.importModal.rowLabel', { row: err.row })} — {err.driver}: {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pied */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={handleClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
          >
            {summary ? t('drivers.importModal.close') : t('drivers.importModal.cancel')}
          </button>
          {parsedData && !summary && (
            <button
              onClick={handleImport}
              disabled={importing || parsedData.length === 0}
              className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Upload className="w-5 h-5" />
              {importing ? t('drivers.importModal.importing') : t('drivers.importModal.importAction', { count: parsedData.length })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default DriverImportModal;
